import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import ForumNameSchema from "notifications/message-notifications/forum-info/forum-name-schema";
import { z } from "zod";

export type ForumInfoJSON = z.infer<typeof ForumInfoSchema.ForumInfoJSONZ>;
export type ForumInfoParams = z.infer<typeof ForumInfoSchema.ForumInfoZ>;

export default class ForumInfoSchema {
  public static readonly ForumInfoJSONZ = z.object({
    id: GuidSchema.GuidJSONZ,
    name: ForumNameSchema.ForumNameJSONZ,
    entityClass: ForumEntityClassSchema.ForumEntityClassJSONZ,
    entityId: GuidSchema.GuidJSONZ.nullish(),
  });
  public static readonly ForumInfoZ = z.object({
    id: z.instanceof(Guid),
    name: z.instanceof(ForumName),
    entityClass: z.instanceof(ForumEntityClass),
    entityId: z.instanceof(Guid).nullable(),
  });

  public static validate(values: unknown): ForumInfoParams {
    return parseOrThrow(ForumInfoSchema.ForumInfoZ, values, {
      logError: true,
      schemaName: "ForumInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ForumInfoJSON {
    return parseOrThrow(ForumInfoSchema.ForumInfoJSONZ, values, {
      logError: true,
      schemaName: "ForumInfoSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is ForumInfoJSON {
    try {
      ForumInfoSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): ForumInfoParams {
    const parsed = this.validateJSON(data);
    return ForumInfoSchema.codec.decode(parsed);
  }

  public static serialize(forumInfo: ForumInfoParams): ForumInfoJSON {
    const parsed = ForumInfoSchema.validate(forumInfo);
    return ForumInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ForumInfoSchema.ForumInfoJSONZ, ForumInfoSchema.ForumInfoZ, {
    decode: (data) => ({
      id: new Guid(GuidSchema.deserialize(data.id)),
      name: new ForumName(ForumNameSchema.deserialize(data.name)),
      entityClass: new ForumEntityClass(ForumEntityClassSchema.deserialize(data.entityClass)),
      entityId: Guid.fromJSON(data.entityId) ?? null,
    }),
    encode: (forumInfo) => ({
      id: forumInfo.id.toJSON(),
      name: forumInfo.name.toJSON(),
      entityClass: forumInfo.entityClass.toJSON(),
      entityId: forumInfo.entityId?.toJSON(),
    }),
  });
}
